import type { ReactNode } from 'react';
import { CheckCircle, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface StateBlockProps {
  icon: PhosphorIcon;
  title: string;
  /** One or two plain sentences. */
  description?: string;
  /** The one main action, if there is one. */
  action?: ReactNode;
  className?: string;
}

/**
 * The layout every Figma P01 state shares (empty, error, no results, 403, 404): a 48px tile with a glyph, a title, a
 * sentence, one action. Use the named components below rather than this directly.
 */
export function StateBlock({ icon, title, description, action, className }: StateBlockProps) {
  return (
    <div
      className={cx(
        'flex flex-col items-center gap-(--mbm-space-2) px-(--mbm-space-6) py-(--mbm-space-9) text-center',
        className,
      )}
    >
      <span
        aria-hidden
        className="flex size-(--mbm-size-state-tile) items-center justify-center rounded-(--mbm-component-radius-state-tile) bg-surface-tint text-text-primary"
      >
        <Icon icon={icon} size="state" />
      </span>
      <h3 className="text-heading-h4 text-text-primary">{title}</h3>
      {description ? (
        <p className="max-w-(--mbm-layout-state-text-width) text-body-small text-text-secondary">{description}</p>
      ) : null}
      {action ? <div className="mt-(--mbm-space-2)">{action}</div> : null}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  /** Say what will show up here and how it gets here. */
  description?: string;
  /** The primary action, if there is one (one at most). */
  action?: ReactNode;
  /** Defaults to a tick: "nothing waiting". Pass the page's own glyph when it has one (a key for the vault). */
  icon?: PhosphorIcon;
  className?: string;
}

/** Nothing here yet (Figma P01 "Empty"): what will show up, and how it gets here. */
export function EmptyState({ title, description, action, icon = CheckCircle, className }: EmptyStateProps) {
  return <StateBlock icon={icon} title={title} description={description} action={action} className={className} />;
}
